import React, { useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { all_routes } from "../../router/all_routes";
import { FeeStatusBadge } from "../../feescollection/components/FeeStatusBadge";
import FeeConfigModal from "../components/FeeConfigModal";
import PredefinedDateRanges from "../../../core/common/datePicker";
import CommonSelect from "../../../core/common/commonSelect";
import { useFeeType } from "../hooks/useFeeType";

// Define the allowed status types
type FeeTypeStatus = "active" | "inactive";

interface FeeTypeData {
  id: string;
  type: string;
  description: string;
  status: FeeTypeStatus;
}

const FeeTypes = () => {
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedFeeTypeId, setSelectedFeeTypeId] = useState<string | null>(
    null
  );

  const { getFeeTypeState, paginationData } = useFeeType({
    page: pageNumber,
    size: recordsPerPage,
    search,
  });
  const { feetypes, isLoading, error } = getFeeTypeState();

  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedFeeType, setSelectedFeeType] = useState("all");

  const handleDeleteClick = (id: string) => {
    setSelectedFeeTypeId(id);
  };

  const columns = [
    {
      title: "S. No",
      render: (_: any, __: any, index: number) => index + 1,
      sorter: (a: any, b: any) => a.id - b.id,
    },
    {
      title: "Fee Type",
      dataIndex: "type",
      render: (type: string) => {
        switch (type) {
          case "coursefee":
            return "Course Fee";
          case "busfee":
            return "Bus Fee";
          case "libraryfee":
            return "Library Fee";
          default:
            return "Registration Fee";
        }
      },
      sorter: (a: FeeTypeData, b: FeeTypeData) => a.type.localeCompare(b.type),
    },
    {
      title: "Description",
      dataIndex: "description",
      sorter: (a: FeeTypeData, b: FeeTypeData) =>
        a.description.localeCompare(b.description),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: FeeTypeStatus) => (
        <span
          className={`badge badge-soft-${
            status === "active" ? "success" : "danger"
          } d-inline-flex align-items-center`}
        >
          <i className="ti ti-circle-filled fs-5 me-1"></i>
          {status}
        </span>
      ),
      sorter: (a: FeeTypeData, b: FeeTypeData) =>
        a.status.localeCompare(b.status),
    },
    {
      title: "Action",
      render: (record: FeeTypeData) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to={`${all_routes.editFeeType.replace(
                    ":id",
                    record.id.toString()
                  )}`}
                >
                  <i className="ti ti-edit me-2" />
                  Edit
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#delete-modal"
                  onClick={() => handleDeleteClick(record.id)}
                >
                  <i className="ti ti-trash me-2" />
                  Delete
                </Link>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  // Add fee type options
  const feeTypeOptions = [
    { value: "all", label: "All Types" },
    { value: "registration", label: "Registration Fee" },
    { value: "tuition", label: "Tuition Fee" },
    { value: "exam", label: "Exam Fee" },
    { value: "material", label: "Material Fee" },
  ];

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "pending", label: "Pending" },
  ];

  const handleApplyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    console.log("Applying filters...");
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    setPageNumber(page);
    if (pageSize) setRecordsPerPage(pageSize);
  };

  const handleSearchChange = (text: string) => {
    setSearch(text);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Fee Types</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">Management</li>
                <li className="breadcrumb-item active">Fee Types</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <div className="mb-2">
              <Link
                to={all_routes.addFeeType}
                className="btn btn-primary d-flex align-items-center"
              >
                <i className="ti ti-square-rounded-plus me-2" />
                Add Fee Type
              </Link>
            </div>
          </div>
        </div>

        {/* Fee Types Table */}
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">Fee Types List</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="dropdown mb-3 me-2">
                <Link
                  to="#"
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                >
                  <i className="ti ti-filter me-2" />
                  Filter
                </Link>
                <div className="dropdown-menu drop-width">
                  <form>
                    <div className="d-flex align-items-center border-bottom p-3">
                      <h4>Filter</h4>
                    </div>
                    <div className="p-3 border-bottom">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Fee Type</label>
                            <CommonSelect
                              className="select"
                              options={feeTypeOptions}
                              value={feeTypeOptions.find(
                                (opt) => opt.value === selectedFeeType
                              )}
                              onChange={(option) =>
                                setSelectedFeeType(option?.value || "all")
                              }
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Status</label>
                            <CommonSelect
                              className="select"
                              options={statusOptions}
                              value={statusOptions.find(
                                (opt) => opt.value === selectedStatus
                              )}
                              onChange={(option) =>
                                setSelectedStatus(option?.value || "all")
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 d-flex align-items-center justify-content-end">
                      <Link to="#" className="btn btn-light me-3">
                        Reset
                      </Link>
                      <Link
                        to="#"
                        className="btn btn-primary"
                        onClick={handleApplyClick}
                      >
                        Apply
                      </Link>
                    </div>
                  </form>
                </div>
              </div>

              {/* Add Sort By Dropdown */}
              <div className="dropdown mb-3">
                <Link
                  to="#"
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  <i className="ti ti-sort-ascending-2 me-2" />
                  Sort by A-Z
                </Link>
                <ul className="dropdown-menu p-3">
                  <li>
                    <Link to="#" className="dropdown-item rounded-1 active">
                      Ascending
                    </Link>
                  </li>
                  <li>
                    <Link to="#" className="dropdown-item rounded-1">
                      Descending
                    </Link>
                  </li>
                  <li>
                    <Link to="#" className="dropdown-item rounded-1">
                      Recently Viewed
                    </Link>
                  </li>
                  <li>
                    <Link to="#" className="dropdown-item rounded-1">
                      Recently Added
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="card-body p-0 py-3">
            {isLoading ? (
              <div className="text-center">Loading fee types...</div>
            ) : error ? (
              <div className="alert alert-danger m-3">
                Error loading fee data: {error.toString()}
              </div>
            ) : (
              <Table
                dataSource={feetypes}
                columns={columns}
                Selection={false}
                pagination={{
                  current: pageNumber,
                  pageSize: recordsPerPage,
                  total: paginationData.totalItems || 0,
                  onChange: handlePageChange,
                  pageSizeOptions: [10, 20, 30],
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Add Fee Type Modal */}
      <FeeConfigModal type="type" id={selectedFeeTypeId} />
    </div>
  );
};

export default FeeTypes;
